import {
  DialogDrawerDescription,
  DialogDrawerFooter,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { Textarea } from '@heidi/design-system/textarea';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { Info, X } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formFieldCharCountMessages } from './form-field-char-count.messages';
import { sharedActionMessages } from './shared-action.messages';
import {
  SHORTCUT_DESCRIPTION_MAX_LENGTH,
  SHORTCUT_NAME_MAX_LENGTH,
  SHORTCUT_PROMPT_MAX_LENGTH,
} from './shortcut-constants';

export interface ShortcutCreateViewProps {
  createName: string;
  onCreateNameChange: (value: string) => void;
  createPrompt: string;
  onCreatePromptChange: (value: string) => void;
  createDescription: string;
  onCreateDescriptionChange: (value: string) => void;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
  onClose: () => void;
  /** When true, displays "Edit" title instead of "Create shortcut" */
  isEditing?: boolean;
}

export const ShortcutCreateView = ({
  createName,
  onCreateNameChange,
  createPrompt,
  onCreatePromptChange,
  createDescription,
  onCreateDescriptionChange,
  canSave,
  onSave,
  onCancel,
  onClose,
  isEditing = false,
}: ShortcutCreateViewProps) => {
  const intl = useIntl();

  const title = isEditing
    ? intl.formatMessage({
        defaultMessage: 'Edit',
        id: '4QItkWoofd',
        description: 'Dialog title when editing an existing Ask Heidi shortcut',
      })
    : intl.formatMessage({
        defaultMessage: 'Create shortcut',
        id: 'esSoWz4sGl',
        description: 'Dialog title when creating a new Ask Heidi shortcut',
      });

  return (
    <>
      <div className="flex items-center justify-between px-6 pt-6">
        <DialogDrawerTitle>{title}</DialogDrawerTitle>
        <DialogDrawerDescription className="sr-only">{title}</DialogDrawerDescription>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClose}
          aria-label={intl.formatMessage({
            defaultMessage: 'Close',
            id: 'pSMSWkU3/I',
            description:
              'Accessibility label for closing the create or edit shortcut dialog',
          })}
        >
          <X />
        </Button>
      </div>
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pt-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="shortcut-name" className="text-text-secondary">
            <FormattedMessage
              defaultMessage="Shortcut name"
              id="dwG5tHJvff"
              description="Label for the shortcut name input field"
            />
          </Label>
          <Input
            id="shortcut-name"
            data-testid="evidence-shortcut-name-input"
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. Patient education',
              id: 'eNI25F3cjH',
              description: 'Placeholder hint for naming a custom Ask Heidi shortcut',
            })}
            value={createName}
            onChange={(e) => onCreateNameChange(e.target.value)}
          />
          <TypographyCaption
            className={cn(
              'self-end',
              createName.length > SHORTCUT_NAME_MAX_LENGTH
                ? 'text-validation-destructive'
                : 'text-text-tertiary'
            )}
          >
            <FormattedMessage
              {...formFieldCharCountMessages.charCount}
              values={{ current: createName.length, max: SHORTCUT_NAME_MAX_LENGTH }}
            />
          </TypographyCaption>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="flex items-center gap-1">
            <Label htmlFor="shortcut-prompt" className="text-text-secondary">
              <FormattedMessage
                defaultMessage="Prompt"
                id="cOUKkbcRbR"
                description="Label for the shortcut prompt textarea"
              />
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="size-3 cursor-help text-text-tertiary" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-60">
                <TooltipArrow />
                <TypographyCaption>
                  <FormattedMessage
                    defaultMessage="This is the instruction Heidi follows when you use this shortcut. Write it like you’d brief a colleague."
                    id="bpmb1RYNhz"
                    description="Tooltip explaining what to write in the shortcut prompt field"
                  />
                </TypographyCaption>
              </TooltipContent>
            </Tooltip>
          </span>
          <Textarea
            id="shortcut-prompt"
            data-testid="evidence-shortcut-prompt-input"
            placeholder={intl.formatMessage({
              defaultMessage:
                'e.g. Explain this patient’s diagnosis and management plan in plain language they can understand. Include lifestyle advice and warning signs to watch for.',
              id: '7AMqabxD1t',
              description: 'Placeholder example text for the shortcut prompt textarea',
            })}
            value={createPrompt}
            onChange={(e) => onCreatePromptChange(e.target.value)}
            rows={6}
            className={cn(
              'resize-y',
              createPrompt.length > SHORTCUT_PROMPT_MAX_LENGTH &&
                'border-validation-destructive'
            )}
          />
          <TypographyCaption
            className={cn(
              'self-end',
              createPrompt.length > SHORTCUT_PROMPT_MAX_LENGTH
                ? 'text-validation-destructive'
                : 'text-text-tertiary'
            )}
          >
            <FormattedMessage
              {...formFieldCharCountMessages.charCount}
              values={{ current: createPrompt.length, max: SHORTCUT_PROMPT_MAX_LENGTH }}
            />
          </TypographyCaption>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="shortcut-description" className="text-text-secondary">
            <FormattedMessage
              defaultMessage="Description"
              id="Vev3pocIEZ"
              description="Label for the optional shortcut description textarea"
            />
          </Label>
          <Textarea
            id="shortcut-description"
            data-testid="evidence-shortcut-description-input"
            placeholder={intl.formatMessage({
              defaultMessage: 'e.g. Plain-language explanation for the patient',
              id: 'tqCHahJ1nD',
              description: 'Placeholder hint for the shortcut description field',
            })}
            value={createDescription}
            onChange={(e) => onCreateDescriptionChange(e.target.value)}
            rows={3}
            className={cn(
              'resize-y',
              createDescription.length > SHORTCUT_DESCRIPTION_MAX_LENGTH &&
                'border-validation-destructive'
            )}
          />
          <TypographyCaption
            className={cn(
              'self-end',
              createDescription.length > SHORTCUT_DESCRIPTION_MAX_LENGTH
                ? 'text-validation-destructive'
                : 'text-text-tertiary'
            )}
          >
            <FormattedMessage
              {...formFieldCharCountMessages.charCount}
              values={{
                current: createDescription.length,
                max: SHORTCUT_DESCRIPTION_MAX_LENGTH,
              }}
            />
          </TypographyCaption>
        </div>
      </div>
      <DialogDrawerFooter className="px-6 py-4">
        <Button variant="ghost" size="xs" onClick={onCancel}>
          <FormattedMessage {...sharedActionMessages.cancel} />
        </Button>
        <Button
          variant="default"
          size="xs"
          onClick={onSave}
          disabled={!canSave}
          data-testid="evidence-shortcut-create-save"
        >
          <FormattedMessage {...sharedActionMessages.save} />
        </Button>
      </DialogDrawerFooter>
    </>
  );
};
